
import React from 'react'
import { debounce } from 'widgets-shared/utilities'
import useEventListener from 'smartstream-widgets/hooks/useEventListener/useEventListener'
import { withComponentContext } from 'widgets-shared/core'
import { Grid } from '@mui/material'
import { compose } from 'widgets-shared/core'
import LogoFlag from 'smartstream-widgets/controls/LogoFlag/LogoFlag'
import MatchState from 'smartstream-widgets/controls/MatchState/MatchState'
import ScoreboardAndRecords from 'smartstream-widgets/components/ScoreboardAndRecords/ScoreboardAndRecords'
import { Scoreboard } from 'smartstream-widgets/components/ScoreboardAndRecords/types'
import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { CompetitorWithFixtureData } from 'smartstream-widgets/types/competitor'
import { MatchState as MatchStateType } from 'smartstream-widgets/types/sport'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'

type Props = {
  classes: {
    [K in string]: string
  },
  competitors: {
    HOME: CompetitorWithFixtureData,
    AWAY: CompetitorWithFixtureData
  },
  isAwayTeamFirst: boolean,
  matchState: MatchStateType,
  scheduledStartTime: string,
  venueName: string,
  showRecords?: boolean
}

const styles = (theme: Theme) => createStyles({
  root: {
    borderBottom: `1px solid ${theme.palette.grey[300]}`
  },
  scoreContainer: {
    padding: `5px 20px`
  },
  matchState: {
    '& p': {
      fontSize: 10
    }
  },
  live:{
    fontSize: 10
  },
  teamName:{
    fontSize: 10
  },
  matchStateContainer: {},
  matchStateContainerLive: {}
})

export function MainLinesHeader(props: Props) {
  const {
    classes,
    competitors,
    isAwayTeamFirst = false,
    matchState,
    scheduledStartTime,
    venueName,
    showRecords = false
  } = props

  const leftCompetitor = competitors[isAwayTeamFirst ? 'AWAY' : 'HOME']
  const leftCompetitorHasRecords = Number.isInteger(leftCompetitor.records.wins) && Number.isInteger(leftCompetitor.records.losses)
  const rightCompetitor = competitors[isAwayTeamFirst ? 'HOME' : 'AWAY']
  const rightCompetitorHasRecords = Number.isInteger(rightCompetitor.records.wins) && Number.isInteger(rightCompetitor.records.losses)

  const recordsAvailable = leftCompetitorHasRecords && rightCompetitorHasRecords
  const svgKeys = [
    `${competitors.AWAY.competitorId}-${competitors.HOME.competitorId}`,
    `${competitors.HOME.competitorId}-${competitors.AWAY.competitorId}`
  ]

  const matchStatus = (matchState && matchState.matchStatus) || 'PreMatch'

  const scoreboardAndRecordsProps: Scoreboard = {
    leftTeam: {
      scores: {
        leftScore: recordsAvailable && leftCompetitor.records.wins,
        rightScore: recordsAvailable && leftCompetitor.records.losses,
        animationDuration: 0
      },
      competitor: leftCompetitor,
      showRecords: showRecords && recordsAvailable && matchStatus === 'InPlay'
    },
    rightTeam: {
      scores: {
        leftScore: recordsAvailable && rightCompetitor.records.wins,
        rightScore: recordsAvailable && rightCompetitor.records.losses,
        animationDuration: 0
      },
      competitor: rightCompetitor,
      showRecords: showRecords && recordsAvailable && matchStatus === 'InPlay'
    },
    liveScore: {
      leftScore: (matchState && matchState.scoreAway),
      rightScore: (matchState && matchState.scoreHome),
      highlight: 'higher'
    },
    isLive: matchStatus === 'InPlay'
  }

  const headerRef: { current: HTMLElement } = React.useRef()
  const [compactDateFormat, setCompactDateFormat] = React.useState(false)

  const useReference = React.useCallback((node: HTMLElement) => {
    headerRef.current = node
    debouncedHandleResize()
  }, [])

  const debouncedHandleResize = debounce(() => {
    setCompactDateFormat(headerRef.current && headerRef.current.clientWidth < 420) 
  }, 200)

  useEventListener('resize', debouncedHandleResize)

  return (
    <Grid container className={classes.root} direction='column' wrap='nowrap' data-testid='main-lines-header'>
      <Grid
        container
        alignItems='center'
        className={`${classes.matchStateContainer} ${matchStatus === 'InPlay' && classes.matchStateContainerLive}`}
        direction='row'
        justifyContent='space-between'
        wrap='nowrap'
        ref={useReference}
      >
        <LogoFlag
          hasGradient
          competitorsBrand={[leftCompetitor.brand]}
          svgKey={svgKeys[0]}
        />
        <MatchState
          className={classes.matchState}     
          classes={{live: classes.live}}     
          matchStatus={matchStatus}
          matchPhase={matchState.phase}
          scheduledStartTime={scheduledStartTime}
          venueName={venueName}
          compactDateFormat={compactDateFormat}
        />
        <LogoFlag
          flip
          hasGradient
          competitorsBrand={[rightCompetitor.brand]}
          svgKey={svgKeys[1]}
        />
      </Grid>
      <ScoreboardAndRecords
        classes={{ root: classes.scoreContainer, teamName: classes.teamName }}
        {...scoreboardAndRecordsProps}
      />
    </Grid>
  )
}

export default compose(
  withComponentContext,
  withStyles(styles, { name: 'MainLinesHeader' , defaultTheme: getDefaultTheme})
)(MainLinesHeader)
